import { useCallback, useEffect, useMemo, useState } from 'react'
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { calendarApi } from '../../api/calendar'
import { RefusedError } from '../../api/client'
import { useSession } from '../../hooks/useSession'
import { useSpoke } from '../../hooks/useSpoke'
import { CALENDAR_VIEWS, calendarParams, parseCalendarParams, periodTitle, rangeFor, startOfDay, stepAnchor, type CalendarViewKind } from '../../lib/calendar'
import { kindOf } from '../../lib/meetingCallouts'
import { scopeWords } from '../../lib/orgs'
import type { MeetingView } from '../../types/calendar'
import { AgendaView } from './AgendaView'
import { Glance } from './Glance'
import { Legend } from './Legend'
import { MeetingDetail } from './MeetingDetail'
import { MonthView } from './MonthView'
import { WeekView } from './WeekView'
import './meetings.css'

// Meetings (PM-010): your calendar, read and resolved, as a month, a week, or an agenda you scroll through.
// One window read drives all three; every meeting carries the facts behind its callouts (who is in the
// room and their part, the account and its pursuits, quiet ties resurfacing, tactics due, the transcript),
// and a meeting opens in place. Every read of the calendar is the calendar-sync job. Scout never writes an event.

const VIEW_LABEL: Record<CalendarViewKind, string> = { month: 'Month', week: 'Week', agenda: 'Agenda' }
const viewKey = (userId: string) => `scout.meetings.view:${userId}`
const rememberedView = (userId: string): CalendarViewKind => {
  const v = window.localStorage.getItem(viewKey(userId))
  return (CALENDAR_VIEWS as string[]).includes(v ?? '') ? (v as CalendarViewKind) : 'month'
}
const ago = (iso: string, now: Date) => {
  const mins = Math.max(0, Math.round((now.getTime() - new Date(iso).getTime()) / 60_000))
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins} min ago`
  const hours = Math.round(mins / 60)
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`
  const days = Math.round(hours / 24)
  return `${days} day${days === 1 ? '' : 's'} ago`
}

export function Meetings() {
  const spoke = useSpoke()
  const navigate = useNavigate()
  const { user } = useSession()
  const [params, setParams] = useSearchParams()
  const now = useMemo(() => new Date(), [])
  const userId = user?.id ?? 'anon'
  const { view, anchor } = useMemo(() => parseCalendarParams(params, rememberedView(userId), now), [params, userId, now])
  const range = useMemo(() => rangeFor(view, anchor), [view, anchor])

  const cal = useQuery({ queryKey: ['calendar', spoke], queryFn: () => calendarApi.view(spoke), staleTime: 15_000 })
  const q = useQuery({
    queryKey: ['calendar-range', spoke, range.from.getTime(), range.to.getTime()],
    queryFn: () => calendarApi.range(spoke, range.from, range.to),
    staleTime: 15_000,
    placeholderData: keepPreviousData,
  })
  const connect = useMutation({ mutationFn: () => calendarApi.connect(spoke), onSuccess: (r) => window.location.assign(r.authorizeUrl) })
  const sync = useMutation({ mutationFn: () => calendarApi.sync(spoke), onSuccess: (r) => navigate(`/jobs/${encodeURIComponent(r.job.id)}`) })

  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [prepRequested, setPrepRequested] = useState(false)
  const [clientOnly, setClientOnly] = useState(false)
  const [showLegend, setShowLegend] = useState(false)

  const go = useCallback((v: CalendarViewKind, a: Date) => {
    setParams(calendarParams(v, a), { replace: v === view })
    window.localStorage.setItem(viewKey(userId), v)
  }, [setParams, userId, view])
  // The URL always says where you are, so the page is a deep link from the first paint.
  useEffect(() => { if (!params.get('view') || !params.get('date')) setParams(calendarParams(view, anchor), { replace: true }) }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const connected = q.data?.connected ?? cal.data?.connected ?? false
  const all = q.data?.meetings ?? []
  const meetings = useMemo(() => (clientOnly ? all.filter((m) => kindOf(m) === 'client') : all), [all, clientOnly])
  const selected = useMemo(() => all.find((m) => m.meeting.meetingId === selectedId) ?? null, [all, selectedId])
  const pick = useCallback((m: MeetingView) => setSelectedId((cur) => (cur === m.meeting.meetingId ? null : m.meeting.meetingId)), [])
  const prep = useCallback((m: MeetingView) => { setSelectedId(m.meeting.meetingId); setPrepRequested(true) }, [])
  const openDay = useCallback((day: Date) => go('agenda', startOfDay(day)), [go])
  const error = params.get('error')
  const conn = cal.data?.connection ?? null

  return (
    <section className="page meetings">
      <header className="deals-head">
        <div>
          <p className="kicker"><Link to="/landscape">Digital Landscape</Link>{q.data?.address ? ` · ${q.data.address}` : ''}{scopeWords(q.data?.scope) ? ` · ${scopeWords(q.data?.scope)}` : ''}</p>
          <h1>Meetings</h1>
          <p className="lede">Your calendar, resolved: who is in the room and the part they play, the account and where its pursuit stands, a prep brief before each client meeting, and the transcript after. Scout reads your events and never writes one.</p>
        </div>
        <div className="actions cal-head-actions">
          {connected && (
            <span className={`cal-conn ${cal.data?.activeJobId ? 'syncing' : 'ok'}`}>
              <i className="dot" />
              {cal.data?.activeJobId
                ? <Link to={`/jobs/${encodeURIComponent(cal.data.activeJobId)}`}>Syncing now</Link>
                : conn?.lastSyncAt ? <>Synced {ago(conn.lastSyncAt, now)}{cal.data?.lastJobId && <> · <Link to={`/jobs/${encodeURIComponent(cal.data.lastJobId)}`}>last sync</Link></>}</> : 'Connected, not synced yet'}
            </span>
          )}
          {connected
            ? <button className="button" onClick={() => sync.mutate()} disabled={sync.isPending || !!cal.data?.activeJobId}>{sync.isPending ? 'Starting…' : 'Sync calendar'}</button>
            : <button className="button primary" onClick={() => connect.mutate()} disabled={connect.isPending}>{connect.isPending ? 'Opening Microsoft…' : 'Connect my calendar'}</button>}
        </div>
      </header>
      {error && <div className="refusal"><strong>The calendar did not connect</strong><p>{error}</p></div>}
      {connect.isError && <p className="error">{connect.error instanceof RefusedError ? connect.error.refusal.message : connect.error.message}</p>}
      {sync.isError && <p className="error">{sync.error instanceof RefusedError ? sync.error.refusal.message : sync.error.message}</p>}
      {q.isError && <p className="error">{q.error instanceof RefusedError ? q.error.refusal.message : q.error.message}</p>}

      {q.isPending && cal.isPending && <p className="muted">Loading…</p>}
      {(q.isSuccess || cal.isSuccess) && !connected && (
        <div className="paths">
          <div className="card path-card recommended">
            <div className="path-head"><div><p className="kicker">Connect</p><h2>Microsoft 365 Calendar</h2></div></div>
            <p>One consent screen. Scout asks to <strong>read</strong> your calendar; the sync runs as a job you can watch, and resolves each meeting to the contacts and the account it is with.</p>
            <ul className="promises">
              <li>Reads events only, <strong>never writes</strong> one</li>
              <li>Resolves attendees to your contacts and pipeline</li>
              <li>Ties the scribe library's transcripts to their meetings</li>
            </ul>
            <button className="button primary" onClick={() => connect.mutate()} disabled={connect.isPending}>{connect.isPending ? 'Opening Microsoft…' : 'Connect my calendar'}</button>
          </div>
        </div>
      )}

      {connected && (
        <>
          <Glance view={view} from={range.from} to={range.to} meetings={all} now={now} onPick={pick} onPrep={prep} onOpenDay={openDay} />

          <div className="cal-toolbar">
            <div className="cal-views" role="tablist" aria-label="View">
              {CALENDAR_VIEWS.map((v) => <button key={v} role="tab" aria-selected={v === view} className={`tab ${v === view ? 'active' : ''}`} onClick={() => go(v, anchor)}>{VIEW_LABEL[v]}</button>)}
            </div>
            <div className="cal-nav">
              <button className="button small" onClick={() => go(view, stepAnchor(view, anchor, -1))} aria-label="Previous">‹</button>
              <button className="button small" onClick={() => go(view, startOfDay(now))}>Today</button>
              <button className="button small" onClick={() => go(view, stepAnchor(view, anchor, 1))} aria-label="Next">›</button>
              <h2 className="cal-period">{periodTitle(view, anchor)}{q.isFetching && <span className="muted small"> · loading</span>}</h2>
            </div>
            <div className="cal-filters">
              <label className="toggle"><input type="checkbox" checked={clientOnly} onChange={(e) => setClientOnly(e.target.checked)} /> client meetings only</label>
              <button className={`chip-filter ${showLegend ? 'on' : ''}`} onClick={() => setShowLegend((s) => !s)} aria-expanded={showLegend}>Legend</button>
              <span className="muted small">{q.data ? `${q.data.totalOnFile.toLocaleString()} on file · ${q.data.linkedTranscripts} transcripts tied` : ''}</span>
            </div>
          </div>
          {showLegend && <Legend />}

          <div className={`cal-body ${selected ? 'has-detail' : ''}`}>
            <div className="cal-view">
              {view === 'month' && <MonthView anchor={anchor} meetings={meetings} now={now} selectedId={selectedId} onPick={pick} onOpenDay={openDay} />}
              {view === 'week' && <WeekView anchor={anchor} meetings={meetings} now={now} selectedId={selectedId} onPick={pick} onOpenDay={openDay} />}
              {view === 'agenda' && <AgendaView anchor={anchor} meetings={meetings} now={now} selectedId={selectedId} onPick={pick} onPrep={prep} />}
              {q.isSuccess && all.length === 0 && view !== 'agenda' && <p className="muted small cal-empty-note">Nothing on file for this window. Sync to refresh, or move to a busier {view}.</p>}
            </div>
            {selected && <MeetingDetail m={selected} now={now} onClose={() => setSelectedId(null)} prepRequested={prepRequested} onPrepHandled={() => setPrepRequested(false)} />}
          </div>
        </>
      )}
    </section>
  )
}
